/**
 * 学校名称模糊搜索,≥3 字才允许查询
 * 选择学校后回调 onSelect(school_id, school_name)
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { search, ApiError } from '../lib/api'

interface Props {
  onSelect: (id: number, name: string) => void
  placeholder?: string
  autoFocus?: boolean
}

export function SchoolSearch({ onSelect,   placeholder = '输入至少三个字的学校名称', autoFocus }: Props) {
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState('')
  const [results, setResults] = useState<{ id: number; school_name: string }[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [idx, setIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const canSearch = q.trim().length >= 3

  const doSearch = useCallback(async (query: string) => {
    if (query.trim().length < 3) { setResults([]); return }
    setLoading(true)
    try {
      const r = await search(query, 8)
      setResults(r.schools.map(s => ({ id: s.id, school_name: s.school_name })))
      setIdx(-1)
    } catch { setResults([]) } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    if (q.trim().length < 3) { setResults([]); return }
    timer.current = setTimeout(() => doSearch(q.trim()), 400)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [q, doSearch])

  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function select(idx: number) {
    const r = results[idx]
    if (!r) return
    setSelected(r.school_name)
    setOpen(false)
    setQ('')
    onSelect(r.id, r.school_name)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!results.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(prev => (prev + 1) % results.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(prev => (prev - 1 + results.length) % results.length) }
    else if (e.key === 'Enter' && idx >= 0) { select(idx) }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        className="inp"
        type="text"
        value={selected || q}
        onChange={e => { setSelected(''); setQ(e.target.value); setOpen(true) }}
        onFocus={() => { if (results.length > 0) setOpen(true) }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {selected && (
        <button
          type="button"
          onClick={() => { setSelected(''); setQ(''); onSelect(0, '') }}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}
        >
          ✕
        </button>
      )}
      {open && q.length > 0 && (
        <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, maxHeight: 240, overflowY: 'auto', marginTop: 2 }}>
          {q.length < 3 && <div style={{ padding: '.5rem .75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>至少输 3 个字才能搜</div>}
          {canSearch && loading && <div style={{ padding: '.5rem .75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>搜学校中…</div>}
          {canSearch && !loading && results.length === 0 && <div style={{ padding: '.5rem .75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>没找到。试试学校简称</div>}
          {results.map((r, i) => (
            <div
              key={r.id}
              onClick={() => select(i)}
              onMouseEnter={() => setIdx(i)}
              style={{ padding: '.4rem .75rem', cursor: 'pointer', background: idx === i ? 'var(--bg-deep)' : 'transparent', fontSize: 'var(--fs-body)', borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none' }}
            >
              {r.school_name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
